import React, { type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { PlotTooltip } from './PlotTooltip'

/**
 * TrackBar — the ONE horizontal single-distribution bar primitive shared across the DOM
 * bar families (SingleChoiceChart list, the monadic magnitude lists).
 *
 * A rounded-pill track (`rounded-full`, ~10px tall) with a rounded fill (`rounded-full`,
 * a `min-w` nub so tiny non-zero values stay visible). The track color comes from the
 * `--chart-bar-track` semantic token (both themes) — killing the hardcoded
 * `hsl(0 0% 90%)` / `bg-foreground/10` literals the DOM bars used to hardcode. Following
 * the deck-ui sibling pattern for app tokens (see MatrixChart / chart-colors), the token
 * is referenced with a sane inline fallback so the bar still renders in a context that
 * has not loaded the app token layer.
 *
 * Each bar consumes the shared {@link PlotTooltip} (value leads, label follows) for a
 * hover AND keyboard-focus tooltip, replacing the native `title=` the old markup used.
 * A consumer that already prints the value and the label next to the bar can opt out with
 * `withTooltip={false}`: a tooltip that reveals what is already on screen is a keyboard
 * stop and a hover target that tell the reader nothing.
 *
 * The bar stretches to the full width of its container: a single-column stretch grid
 * blockifies + stretches the `PlotTooltip` inline wrapper to 100%, then the track fills
 * that width. Consumers keep rendering their own label/value row above the bar.
 */
export interface TrackBarProps {
  /** Fill width as a percentage (0–100); clamped defensively. */
  widthPct: number
  /** Fill color (a resolved chart palette color, e.g. `var(--slide-chart-1)` or hsl()). */
  color: string
  /** Leading value for the tooltip (e.g. "12 (34%)") — rendered first. */
  value: ReactNode
  /** Trailing label for the tooltip (e.g. the option name) — rendered after the value. */
  label: ReactNode
  /** Optional extra classes for the track element (layout only; color stays token-driven). */
  trackClassName?: string
  /**
   * Whether the bar carries the shared hover/focus tooltip. Defaults to true.
   * Pass false where the value and the label are already printed beside the bar.
   */
  withTooltip?: boolean
}

export function TrackBar(props: TrackBarProps) {
  const { widthPct, color, value, label, trackClassName, withTooltip = true } = props

  const clampedWidth = Math.max(0, Math.min(100, widthPct))

  const track = (
    <span
      className={cn('relative block h-[10px] w-full rounded-full', trackClassName)}
      style={{ backgroundColor: 'hsl(var(--chart-bar-track, 0 0% 90%))' }}
    >
      <span
        className={cn(
          'absolute inset-0 rounded-full transition-all duration-500 ease-out',
          clampedWidth > 0 && 'min-w-1',
        )}
        style={{ width: `${clampedWidth}%`, backgroundColor: color }}
      />
    </span>
  )

  return (
    <div className="grid w-full grid-cols-1">
      {withTooltip ? (
        <PlotTooltip value={value} label={label}>
          {track}
        </PlotTooltip>
      ) : (
        track
      )}
    </div>
  )
}
